// File: frontend/src/components/FitenkoVerhnyayaPanel.js
// Version: 1.0 (quick-260911-dzd)
// Date: 2026-09-11
// Назначение: верхняя панель арендатора fitenko — ПОИСК и ВЫБОР ОФИСА.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ ЭТО ТРЕБОВАНИЕ, А НЕ ЭКОНОМИЯ
// ═══════════════════════════════════════════════════════════════════════════
// Атлас (стр. 4) рисует в верхней полосе ПЯТЬ узлов: поиск, `+ New client`,
// переключатель языка EN/RU/ES, уведомления, `Ask Katifi`. Здесь их ТРИ.
// Каждое отсутствие — решение с проверяемой причиной, а не недоделка.
//
// 1. ПЕРЕКЛЮЧАТЕЛЯ ЯЗЫКА НЕТ. Ни одной библиотеки перевода во фронте нет
//    (`grep -c "i18next\|react-intl" frontend/package.json` → 0), словарей нет.
//    Кнопка переключала бы СВОЮ ПОДПИСЬ и ничего больше. Это хуже отсутствия:
//    сотрудник решит, что язык интерфейса меняется, и не поймёт, почему
//    ничего не происходит. [[knowledge_decorative_flag]]
//    Отдельно: в атласе языков ТРИ, а у фирмы их ДВА — третьего нет
//    (канон NAP §1). Перенести третий значило бы напечатать неправду.
//
// 2. УВЕДОМЛЕНИЙ НЕТ: ручки уведомлений в API не существует. Колокольчик с
//    нулём рядом — это утверждение «непрочитанного нет», которого никто не
//    измерял. Ноль без знаменателя — отказ прибора, а не значение.
//
// 3. `+ НОВОЕ ОБРАЩЕНИЕ` НЕТ. Ручка `POST /leads/` существует и её зовут ДВА
//    ПУБЛИЧНЫХ виджета (`PublicChatWidget.js:514`, `PublicCalendarWidget.js:101`),
//    а СОТРУДНИЧЕСКОГО маршрута и экрана создания нет. Ссылка в никуда хуже
//    отсутствия — то же правило уже записано в `HistoryPage.js:41-43`.
//    Появится маршрут — появится кнопка, и её заведёт тот, у кого он появится.
//
// 🔴 `Спросить <ИМЯ>` СЮДА НЕ ПЕРЕЕХАЛА. Она уже работает и живёт в
//    `Navbar.js` под тем же рубежом арендатора. Перенос разметки не добавил бы
//    ничего, кроме риска задеть работающее, и сдвинул бы её отпечаток без
//    единого изменения поведения.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ПОЧЕМУ ВЫБОР ОФИСА МЕНЯЕТ НАБЛЮДАЕМОЕ, А НЕ ТОЛЬКО СВОЮ ПОДПИСЬ
// ═══════════════════════════════════════════════════════════════════════════
// Переключение меняет ВЕСЬ состав карточки: улицу, город, индекс, часы приёма
// и — главное — СТРОКУ О ПУБЛИЧНОСТИ. У головного офиса улицу публиковать
// можно, у филиала нельзя (это жильё), и карточка говорит об этом словами,
// а публичный вид адреса приходит с сервера УЖЕ без улицы. Два офиса
// различаются этим признаком, то есть ветка «улица скрыта» ИСПОЛНЯЕТСЯ, а не
// стоит гипотезой. [[knowledge_unexercised_branch_is_a_hypothesis]]
//
// 🔴 НАЗВАНИЙ ГОРОДОВ ЗДЕСЬ НЕТ ДАЖЕ В ПРОЗЕ, И ЭТО НЕ ПРИДИРКА. Прибор считает
//    вхождения В ФАЙЛЕ, и комментарий для него неотличим от кода: сосед
//    `test_tenant_isolation.py` пропускает строки-комментарии, а сторож
//    `verify_fitenko_ofisy.py` — нет, и он покраснел на первом же прогоне.
//    Исключение в приборе было бы приглашением положить рядом второй литерал,
//    уже настоящий. Города называет КАНОН, а не общий фронт.
//
// Выбор переживает перезагрузку (память браузера). Ключ выбранного офиса
// сверяется с пришедшим списком: офис, которого в каноне больше нет, молча не
// подставляется — берётся головной.
//
// 🔴 НИ ОДНОГО ЗНАЧЕНИЯ ОФИСА ЗДЕСЬ НЕ ЗАПИСАНО. Ни адреса, ни телефона, ни
//    названия города, ни языков. Всё приходит с сервера из файла канона.
//    Литерал арендатора в общем фронте — это данные одного арендатора в коде,
//    который читают все четыре (сторож `test_tenant_isolation.py` смотрит и
//    сюда).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, X } from 'lucide-react';
import apiService, { razobratOfisy, razobratPoisk } from '../services/apiService';

// Память браузера: ключ выбранного офиса. Значение — `kluch` из канона.
const PAMYAT_OFIS = 'fk.ofis';
// Ниже двух символов сервер не ищет (`services/poisk.MIN_DLINA`). Здесь стоит
// то же число, и согласие двух величин проверяет сторож: разойдись они —
// строка молча слала бы запросы, на которые сервер отвечает отказом.
const MIN_DLINA_ZAPROSA = 2;
const ZADERZHKA_MS = 300;

const pamyatChitat = () => {
    try {
        return window.localStorage.getItem(PAMYAT_OFIS);
    } catch (e) {
        return null;   // приватный режим браузера — не повод падать
    }
};

const pamyatPisat = (kluch) => {
    try {
        window.localStorage.setItem(PAMYAT_OFIS, kluch);
    } catch (e) {
        /* память недоступна: выбор проживёт до перезагрузки, и это не поломка */
    }
};

// ─────────────────────────────────────────────────────────────────────────
// ВЫБОР ОФИСА
// ─────────────────────────────────────────────────────────────────────────
const VyborOfisa = () => {
    const [sostoyanie, setSostoyanie] = useState({ rod: 'zhdyom' });
    const [vybran, setVybran] = useState(pamyatChitat());
    const [otkryto, setOtkryto] = useState(false);
    const ramka = useRef(null);

    useEffect(() => {
        let zhiv = true;
        apiService.getOfisy()
            .then((otvet) => { if (zhiv) setSostoyanie(razobratOfisy(otvet)); })
            .catch((e) => {
                if (zhiv) setSostoyanie({ rod: 'otkaz', tekst: e.message || 'офисы не пришли' });
            });
        return () => { zhiv = false; };
    }, []);

    useEffect(() => {
        const naKlik = (e) => {
            if (ramka.current && !ramka.current.contains(e.target)) setOtkryto(false);
        };
        document.addEventListener('mousedown', naKlik);
        return () => document.removeEventListener('mousedown', naKlik);
    }, []);

    const ofisy = sostoyanie.rod === 'spisok' ? sostoyanie.ofisy : [];

    // 🔴 Ключ из памяти сверяется со списком. Офис, которого в каноне больше
    //    нет (переезд), молча не подставляется — берётся головной.
    const tekushchiy = useMemo(() => {
        if (!ofisy.length) return null;
        return ofisy.find((o) => o.kluch === vybran)
            || ofisy.find((o) => o.glavnyy)
            || ofisy[0];
    }, [ofisy, vybran]);

    const vybrat = (kluch) => {
        setVybran(kluch);
        pamyatPisat(kluch);
        setOtkryto(false);
    };

    if (sostoyanie.rod === 'otkaz') {
        // Отказ говорит словами. Пустое место означало бы «офисов нет».
        return (
            <span className="fk-panel-ofis-otkaz" title={sostoyanie.tekst}>
                офисы не прочитаны
            </span>
        );
    }
    if (sostoyanie.rod === 'zhdyom') return null;
    if (!tekushchiy) return null;   // законная пустота: у арендатора офисов нет

    return (
        <div className="fk-panel-ofis" ref={ramka}>
            <button
                type="button"
                className="fk-panel-ofis-knopka"
                onClick={() => setOtkryto(!otkryto)}
                aria-expanded={otkryto}
            >
                <MapPin size={16} aria-hidden="true" />
                <span className="fk-panel-ofis-imya">{tekushchiy.nazvanie}</span>
                <span className="fk-panel-ofis-rol">
                    {tekushchiy.glavnyy ? 'главный' : 'филиал'}
                </span>
            </button>

            {otkryto && (
                <div className="fk-panel-ofis-panel">
                    <div className="fk-panel-ofis-spisok">
                        {ofisy.map((o) => (
                            <button
                                key={o.kluch}
                                type="button"
                                className={`fk-panel-ofis-punkt${o.kluch === tekushchiy.kluch ? ' is-vybran' : ''}`}
                                onClick={() => vybrat(o.kluch)}
                            >
                                <span className="fk-panel-ofis-imya">{o.nazvanie}</span>
                                <span className="fk-panel-ofis-rol">
                                    {o.glavnyy ? 'главный' : 'филиал'}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Карточка МЕНЯЕТСЯ ЦЕЛИКОМ при выборе — это и есть
                        наблюдаемое следствие переключения. */}
                    <dl className="fk-panel-ofis-karta">
                        <dt>Фирма</dt><dd>{tekushchiy.firma}</dd>
                        <dt>Адрес</dt><dd>{tekushchiy.adres_vnutri}</dd>
                        <dt>Телефон</dt><dd>{tekushchiy.telefon}</dd>
                        <dt>Почта</dt><dd>{tekushchiy.pochta}</dd>
                        <dt>Часы</dt><dd>{tekushchiy.chasy}</dd>
                        <dt>Языки</dt><dd>{(tekushchiy.yazyki || []).join(', ')}</dd>
                    </dl>

                    {/* 🔴 ПРИЗНАК ПУБЛИЧНОСТИ НАЗЫВАЕТСЯ ВСЛУХ, а публичный вид
                        адреса ПОКАЗЫВАЕТСЯ. Без этого сотрудник скопировал бы
                        в объявление улицу, которую публиковать нельзя, и
                        молчаливое допущение сработало бы против него. */}
                    <div className={`fk-panel-ofis-naruzhu${tekushchiy.ulica_publichna ? '' : ' is-zakryt'}`}>
                        <span className="fk-panel-ofis-naruzhu-metka">
                            {tekushchiy.ulica_publichna
                                ? 'улицу публиковать можно'
                                : 'улицу публиковать НЕЛЬЗЯ — это жильё'}
                        </span>
                        <span className="fk-panel-ofis-naruzhu-adres">{tekushchiy.adres_naruzhu}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────
// ПОИСК
// ─────────────────────────────────────────────────────────────────────────
const StrokaPoiska = () => {
    const [zapros, setZapros] = useState('');
    const [sostoyanie, setSostoyanie] = useState({ rod: 'pokoy' });
    const [otkryto, setOtkryto] = useState(false);
    const ramka = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const naKlik = (e) => {
            if (ramka.current && !ramka.current.contains(e.target)) setOtkryto(false);
        };
        document.addEventListener('mousedown', naKlik);
        return () => document.removeEventListener('mousedown', naKlik);
    }, []);

    const sprosit = useCallback((q) => {
        apiService.poisk(q)
            .then((otvet) => setSostoyanie(razobratPoisk(otvet)))
            .catch((e) => setSostoyanie({
                rod: 'otkaz',
                // 🔴 403 и 500 — РАЗНЫЕ беды, и код ответа их различает.
                tekst: e.status === 403
                    ? 'поиск по людям и делам доступен только сотрудникам фирмы'
                    : (e.message || 'поиск не ответил'),
            }));
    }, []);

    useEffect(() => {
        const q = zapros.trim();
        if (q.length < MIN_DLINA_ZAPROSA) {
            setSostoyanie({ rod: 'pokoy' });
            return undefined;
        }
        setSostoyanie({ rod: 'ishchem' });
        const t = setTimeout(() => sprosit(q), ZADERZHKA_MS);
        return () => clearTimeout(t);
    }, [zapros, sprosit]);

    const perejti = (kuda) => {
        setOtkryto(false);
        navigate(kuda);
    };

    const nahodki = sostoyanie.rod === 'nahodki' ? sostoyanie : null;
    const pokazyvat = otkryto && zapros.trim().length >= MIN_DLINA_ZAPROSA;

    return (
        <div className="fk-panel-poisk" ref={ramka}>
            <div className="fk-panel-poisk-pole">
                <Search size={16} aria-hidden="true" />
                <input
                    type="search"
                    value={zapros}
                    onChange={(e) => { setZapros(e.target.value); setOtkryto(true); }}
                    onFocus={() => setOtkryto(true)}
                    placeholder="Имя, почта, телефон или номер дела"
                    aria-label="Поиск по обращениям, людям и делам"
                />
                {zapros && (
                    <button type="button" className="fk-panel-poisk-sbros"
                            onClick={() => { setZapros(''); setOtkryto(false); }}
                            aria-label="Очистить">
                        <X size={14} aria-hidden="true" />
                    </button>
                )}
            </div>

            {pokazyvat && (
                <div className="fk-panel-poisk-vydacha">
                    {sostoyanie.rod === 'ishchem' && (
                        <p className="fk-panel-poisk-slovo">ищем…</p>
                    )}

                    {sostoyanie.rod === 'otkaz' && (
                        <p className="fk-panel-poisk-otkaz">
                            {/* Отказ измерителя НЕ выдаётся за «ничего не найдено». */}
                            Поиск не ответил: {sostoyanie.tekst}
                        </p>
                    )}

                    {nahodki && nahodki.vsegoNahodok === 0 && (
                        <p className="fk-panel-poisk-slovo">Ничего не нашлось.</p>
                    )}

                    {nahodki && nahodki.lyudi.length > 0 && (
                        <>
                            <p className="fk-panel-poisk-razdel">
                                Люди{nahodki.usechenie.obrashcheniya || nahodki.usechenie.uchyotnye_zapisi
                                    ? ' — показаны не все' : ''}
                            </p>
                            {nahodki.lyudi.map((c, i) => {
                                const pervoe = c.obrashcheniya[0];
                                const kuda = pervoe ? pervoe.kuda : '/user-management';
                                return (
                                    <button
                                        key={`${c.person_id || 'net'}-${i}`}
                                        type="button"
                                        className="fk-panel-poisk-stroka"
                                        onClick={() => perejti(kuda)}
                                    >
                                        <span className="fk-panel-poisk-imya">{c.imya || '— без имени —'}</span>
                                        <span className="fk-panel-poisk-hvost">
                                            {c.pochta || c.telefon || ''}
                                            {c.obrashcheniya.length > 0 && ` · обращений ${c.obrashcheniya.length}`}
                                            {c.uchyotnye_zapisi.length > 0 && ' · есть учётная запись'}
                                            {pervoe && pervoe.slit_v && ' · дубль, объединён'}
                                        </span>
                                    </button>
                                );
                            })}
                        </>
                    )}

                    {nahodki && nahodki.dela.length > 0 && (
                        <>
                            <p className="fk-panel-poisk-razdel">
                                Дела{nahodki.usechenie.dela ? ' — показаны не все' : ''}
                            </p>
                            {nahodki.dela.map((d) => (
                                <button key={d.id} type="button"
                                        className="fk-panel-poisk-stroka"
                                        onClick={() => perejti(d.kuda)}>
                                    <span className="fk-panel-poisk-imya">{d.nomer}</span>
                                    <span className="fk-panel-poisk-hvost">
                                        {d.zagolovok}{d.klient && ` · ${d.klient}`}
                                    </span>
                                </button>
                            ))}
                        </>
                    )}

                    {/* 🔴 ПОДПИСЬ ОБ ОХВАТЕ ПРИХОДИТ С СЕРВЕРА И ПЕЧАТАЕТСЯ КАК
                        ЕСТЬ. Набранная здесь руками, она разошлась бы с тем, что
                        сервер действительно смотрит, — и разошлась бы молча. */}
                    {nahodki && (
                        <p className="fk-panel-poisk-ohvat">
                            Ищем: {nahodki.ohvacheno.join('; ')}.
                            <br />
                            Не ищем: {nahodki.neOhvacheno.join(', ')}.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// 🔴 ПАНЕЛЬ — СЛОЙ. Рубеж арендатора здесь НЕ ЗАВОДИТСЯ: он стоит выше, в
//    `Navbar.js`, тем же условием `isFitenko`, что и колонка. Второй рубеж
//    означал бы двух держателей одного решения, и разошлись бы они молча.
const FitenkoVerhnyayaPanel = () => (
    <div className="fk-panel">
        <StrokaPoiska />
        <VyborOfisa />
    </div>
);

export default FitenkoVerhnyayaPanel;
export { MIN_DLINA_ZAPROSA, PAMYAT_OFIS };
